import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { selectOwnerEnterprise } from '../domain/enterprise.rules';
import type { Enterprise } from '../domain/enterprise.types';
import { enterpriseRepository } from '../infrastructure/enterprise.repository';

interface ActiveEnterpriseContextValue {
  enterprise: Enterprise | null;
  loading: boolean;
  refreshEnterprise: () => Promise<void>;
}

const ActiveEnterpriseContext = createContext<ActiveEnterpriseContextValue | null>(null);

export function ActiveEnterpriseProvider({ children }: { children: ReactNode }) {
  const token = localStorage.getItem('token');
  const [enterprise, setEnterprise] = useState<Enterprise | null>(null);
  const [loading, setLoading] = useState(() => Boolean(token));

  useEffect(() => {
    if (!token) return;

    enterpriseRepository
      .getAll()
      .then((enterprises) => {
        const ownerEnterprise = selectOwnerEnterprise(enterprises);
        if (ownerEnterprise) setEnterprise(ownerEnterprise);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [token]);

  const refreshEnterprise = useCallback(async () => {
    try {
      const ownerEnterprise = selectOwnerEnterprise(await enterpriseRepository.getAll());
      if (ownerEnterprise) setEnterprise(ownerEnterprise);
    } catch {
      // Refresh remains silent and preserves the previous enterprise.
    }
  }, []);

  const value = useMemo<ActiveEnterpriseContextValue>(
    () => ({ enterprise, loading, refreshEnterprise }),
    [enterprise, loading, refreshEnterprise]
  );

  return (
    <ActiveEnterpriseContext.Provider value={value}>
      {children}
    </ActiveEnterpriseContext.Provider>
  );
}

export function useActiveEnterprise(): ActiveEnterpriseContextValue {
  const context = useContext(ActiveEnterpriseContext);
  if (!context) {
    throw new Error('useActiveEnterprise must be used within an ActiveEnterpriseProvider');
  }
  return context;
}